import _ from 'lodash'

const ignoreFields = ['hasWords']

type Inputs = Record<string, any>

/**
 * Normalize object values commonly used in the app
 *
 * * Removes non-strings
 * * Trims leading or trailing spaces
 * * Combines multiple consecutive spaces into a single space
 * * Removes null, undefined, empty strings
 * * Removes spaces after commas
 * * Wraps any strings with spaces in double quotes EXCEPT in the `hasWords` property
 *
 * @example
 * sanitizeInputs({ make: 'Hello, World', hasWords: ' foo  bar ', test: 1 })
 * // returns { make: '"Hello,World"', hasWords: 'foo bar' }
 *
 * @param inputs: Record<string, any>
 * @returns Record<string, string>
 */
export const sanitizeInputs = <T extends Inputs>(inputs: T): Partial<T> => {
    if (!inputs) {
        return {}
    }

    return (
        _(inputs)
            .mapValues((v: unknown) => (typeof v === 'string' ? v : '')) // Removes non-string values
            // eslint-disable-next-line @typescript-eslint/unbound-method
            .mapValues(_.trim) // Trim leading or trailing spaces. Also converts null/undefined to empty string
            .mapValues((v: string) => v.replace(/\s\s+/g, ' ')) // Multiple spaces to a single space
            .pickBy() // Removes empty strings
            .mapValues(
                (v: string) => (v ? v.replace(/,\s/g, ',') : ''), // Remove spaces after commas
            )
            .mapValues((v: string, k: string) => (v.includes(' ') && !ignoreFields.includes(k) ? `"${v}"` : v)) // Wrap strings with spaces in quotes
            .valueOf() as Partial<T>
    )
}
